import React, { useState, useEffect } from 'react';
import { getProjectBuildings, deleteBuilding, getBuildingImages, getProject, getBuildingReports, createBuildingArchive } from '../services/api';
import BuildingModal from './BuildingModal';
import './BuildingManagement.css';

const BuildingManagement = ({ projectId, projectName, onNavigate }) => {
  const isDev = process.env.NODE_ENV === 'development';
  const canProxy = typeof window !== 'undefined' && (window.location.port === '3000' || window.location.port === '3001');
  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    ((isDev && canProxy)
      ? ''
      : (typeof window !== 'undefined'
          ? `${window.location.protocol}//${window.location.hostname}:8000`
          : 'http://127.0.0.1:8000'));
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [statsByBuilding, setStatsByBuilding] = useState({});
  const [currentProjectType, setCurrentProjectType] = useState(null);
  const [archivingBuildingId, setArchivingBuildingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const toBuildingImageUrl = (url) => {
    if (!url) return '';
    const value = String(url).trim();
    if (!value) return '';
    return value.startsWith('http') ? value : `${API_BASE_URL}${value}`;
  };

  const toBuildingCardImageUrl = (building, url) => {
    const source = toBuildingImageUrl(url);
    if (!source || !building?.image_cover_url || url !== building.image_cover_url) return source;
    const version = encodeURIComponent(building.updated_at || Date.now());
    return `${source}${source.includes('?') ? '&' : '?'}v=${version}`;
  };

  useEffect(() => {
    console.log('BuildingManagement mounted, projectId:', projectId);
    if (projectId) {
      loadBuildings();
      fetchProjectDetails();
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      const project = await getProject(projectId);
      if (project) {
        setCurrentProjectType(project.project_type);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const loadBuildings = async () => {
    try {
      setLoading(true);
      const data = await getProjectBuildings(projectId);
      setBuildings(data);
      setError('');
      // Fetch stats for each building (elevations/images/defects)
      try {
        const statsEntries = await Promise.all(
          (data || []).map(async (b) => {
            try {
              const [res, reportsRes] = await Promise.all([
                getBuildingImages(b.id),
                getBuildingReports(b.id),
              ]);
              const fs = res?.folder_structure || {};
              const reports = Array.isArray(reportsRes?.reports) ? reportsRes.reports : [];
              const elevationsCount = Object.keys(fs).length;
              let imagesCount = 0;
              let defectsCount = 0;
              Object.values(fs).forEach((arr) => {
                imagesCount += Array.isArray(arr) ? arr.length : 0;
                (Array.isArray(arr) ? arr : []).forEach((img) => {
                  defectsCount += Array.isArray(img?.defects) ? img.defects.length : 0;
                });
              });
              const latestReportAt = reports
                .map((report) => report?.created_at)
                .filter(Boolean)
                .sort((a, b) => new Date(b) - new Date(a))[0] || null;
              return [b.id, { elevationsCount, imagesCount, defectsCount, reportCount: reports.length, latestReportAt }];
            } catch (_) {
              return [b.id, { elevationsCount: 0, imagesCount: 0, defectsCount: 0, reportCount: 0, latestReportAt: null }];
            }
          })
        );
        const statsObj = Object.fromEntries(statsEntries);
        setStatsByBuilding(statsObj);
      } catch (_) {
        // ignore stats errors
      }
    } catch (error) {
      console.error('Error loading buildings:', error);
      setError('Failed to load buildings');
    } finally {
      setLoading(false);
    }
  };

  // Removed Add Building flow per new UX; buildings are auto-created elsewhere

  const handleEditBuilding = (building) => {
    setSelectedBuilding(building);
    setShowModal(true);
  };

  const handleDeleteBuilding = async (buildingId) => {
    try {
      await deleteBuilding(buildingId);
      setBuildings(buildings.filter(b => b.id !== buildingId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting building:', error);
      setError('Failed to delete building');
    }
  };

  const handleArchiveBuilding = async (building) => {
    if (!building) return;
    try {
      setArchivingBuildingId(building.id);
      setError('');
      const archive = await createBuildingArchive(building.id);
      const fileCount = archive?.file_count ? ` (${archive.file_count} files)` : '';
      setSuccessMessage(`Archive created for "${building.name}"${fileCount}`);
      await loadBuildings();
    } catch (error) {
      console.error('Error archiving building:', error);
      setError(`Failed to archive building "${building.name}". Please try again.`);
    } finally {
      setArchivingBuildingId(null);
    }
  };

  const handleOpenBuildingArchive = (building) => {
    const archiveUrl = building?.latest_archive_url;
    if (!archiveUrl) {
      setError('No archive found for this building yet.');
      return;
    }
    const targetUrl = String(archiveUrl).startsWith('http') ? archiveUrl : `${API_BASE_URL}${archiveUrl}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSaveBuilding = async (savedBuilding) => {
    if (selectedBuilding) {
      // Update existing building
      setBuildings(buildings.map(b => 
        b.id === savedBuilding.id ? savedBuilding : b
      ));
    } else {
      // Add new building
      setBuildings([...buildings, savedBuilding]);
    }
    setShowModal(false);
    setSelectedBuilding(null);
    await loadBuildings();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const activeBuildings = buildings.filter((building) => !building.archive_exists);
  const archivedBuildingCount = buildings.length - activeBuildings.length;

  const totalReportsGenerated = activeBuildings.reduce((sum, building) => (
    sum + (statsByBuilding[building.id]?.reportCount || 0)
  ), 0);

  const buildingsWithReports = activeBuildings.reduce((sum, building) => (
    sum + ((statsByBuilding[building.id]?.reportCount || 0) > 0 ? 1 : 0)
  ), 0);

  if (loading) {
    return (
      <div className="building-management">
        <div className="loading">Loading buildings...</div>
      </div>
    );
  }

  return (
    <div className="building-management">
      <div className="building-header">
        <div>
          <h3>Buildings in {projectName}</h3>
          <div className="building-report-summary">
            <span className="report-summary-pill">Total Reports Generated: {totalReportsGenerated}</span>
            <span>{buildingsWithReports} of {activeBuildings.length} active buildings have reports</span>
            {archivedBuildingCount > 0 && <span>{archivedBuildingCount} archived</span>}
          </div>
        </div>
        <button
          className="add-building-btn"
          onClick={() => {
            setSelectedBuilding(null);
            setShowModal(true);
          }}
        >
          Add Building
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {activeBuildings.length === 0 ? (
        <div className="no-buildings">
          <p>{archivedBuildingCount > 0 ? 'All buildings in this project are archived.' : 'No buildings found for this project.'}</p>
        </div>
      ) : (
        <div className="buildings-grid">
          {activeBuildings.map(building => {
            const cardImageUrl = building.image_cover_url || building.thumbnail_url;
            return (
            <div 
              key={building.id} 
              className="building-card"
              onClick={() => onNavigate && onNavigate('elevations', { projectId, projectName, buildingId: building.id, buildingName: building.name })}
            >
              {cardImageUrl ? (
                <div className="building-thumb">
                  <img
                    src={toBuildingCardImageUrl(building, cardImageUrl)}
                    alt={`${building.name} cover`}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="thumbnail-placeholder">No images yet</div>
              )}
              <div className="building-info">
                <h4>{building.name}</h4>
                {building.description && (
                  <p className="building-description">{building.description}</p>
                )}
                {building.address && (
                  <p className="building-address">📍 {building.address}</p>
                )}
                <div className="building-details">
                  <span className="floors-count">🏢 {building.floors_count} floors</span>
                  <span className="created-date">Created: {formatDate(building.created_at)}</span>
                </div>
                {(building.created_by_name || building.updated_by_name) && (
                  <div className="building-details">
                    {building.created_by_name && (
                      <span className="created-by">👤 Created by: {building.created_by_name}</span>
                    )}
                    {building.updated_by_name && building.updated_by !== building.created_by && (
                      <span className="updated-by">✏️ Last updated by: {building.updated_by_name}</span>
                    )}
                  </div>
                )}
                <div className="building-details">
                  <span>📐 Elevations: {statsByBuilding[building.id]?.elevationsCount ?? '-'}</span>
                  <span>🖼️ Images: {statsByBuilding[building.id]?.imagesCount ?? '-'}</span>
                  <span>⚠️ Defects: {statsByBuilding[building.id]?.defectsCount ?? '-'}</span>
                </div>
                <div className="building-report-status">
                  <span className={`report-status-chip ${(statsByBuilding[building.id]?.reportCount || 0) > 0 ? 'generated' : 'pending'}`}>
                    {(statsByBuilding[building.id]?.reportCount || 0) > 0 ? 'Report Generated' : 'No Report Yet'}
                  </span>
                  <span className="report-count">
                    Total Reports: {statsByBuilding[building.id]?.reportCount ?? 0}
                  </span>
                  {statsByBuilding[building.id]?.latestReportAt && (
                    <span className="latest-report-date">
                      Latest: {formatDate(statsByBuilding[building.id].latestReportAt)}
                    </span>
                  )}
                </div>
              </div>
              <div className="building-actions">
                <button 
                  className="edit-btn"
                  onClick={(e) => { e.stopPropagation(); handleEditBuilding(building); }}
                >
                  Edit
                </button>
                {/* Render buttons based on Project Type */}
                {(!currentProjectType || currentProjectType === 'Drone & Tapping') && (
                  <>
                    <button 
                      className="edit-btn"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        onNavigate && onNavigate('elevations', { 
                          projectId, 
                          projectName, 
                          buildingId: building.id, 
                          buildingName: building.name,
                          initialImageType: 'drone'
                        }); 
                      }}
                    >
                      Drone Images
                    </button>
                    <button 
                      className="edit-btn"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        onNavigate && onNavigate('elevations', { 
                          projectId, 
                          projectName, 
                          buildingId: building.id, 
                          buildingName: building.name,
                          initialImageType: 'tapping'
                        }); 
                      }}
                    >
                      Tapping Images
                    </button>
                  </>
                )}
                
                {currentProjectType === 'Drone Only' && (
                  <button 
                    className="edit-btn"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onNavigate && onNavigate('elevations', { 
                        projectId, 
                        projectName, 
                        buildingId: building.id, 
                        buildingName: building.name,
                        initialImageType: 'drone'
                      }); 
                    }}
                  >
                    Drone Images
                  </button>
                )}

                {currentProjectType === 'Tapping Only' && (
                  <button 
                    className="edit-btn"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onNavigate && onNavigate('elevations', { 
                        projectId, 
                        projectName, 
                        buildingId: building.id, 
                        buildingName: building.name,
                        initialImageType: 'tapping'
                      }); 
                    }}
                  >
                    Tapping Images
                  </button>
                )}
                
                <button 
                  className="edit-btn"
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('buildingInfo', { projectId, projectName, buildingId: building.id, buildingName: building.name }); }}
                >
                  Info
                </button>
                <button 
                  className="edit-btn"
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('reports', { projectId, projectName, buildingId: building.id, buildingName: building.name }); }}
                >
                  Generate Report
                </button>
                {building.archive_exists && building.latest_archive_url && (
                  <button
                    className="edit-btn"
                    onClick={(e) => { e.stopPropagation(); handleOpenBuildingArchive(building); }}
                  >
                    Open Archive
                  </button>
                )}
                <button
                  className="edit-btn"
                  disabled={archivingBuildingId === building.id}
                  onClick={(e) => { e.stopPropagation(); handleArchiveBuilding(building); }}
                >
                  {archivingBuildingId === building.id ? 'Archiving...' : 'Archive'}
                </button>
                <button 
                  className="delete-btn"
                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(building.id); }}
                >
                  Delete
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Building Modal */}
      <BuildingModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveBuilding}
        building={selectedBuilding}
        projectId={projectId}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this building? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-button"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="delete-button"
                onClick={() => handleDeleteBuilding(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingManagement;
